import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Reveal from "./Reveal";
import { useLang } from "../i18n/LanguageContext";

const STORY = {
  en: {
    eyebrow: "The first collective piece",
    title: "Heidrun",
    sub: "A work made by many hands, carrying one shared story.",
    imageCaption: "Heidrun's mobile, seen from both sides.",
    cta: "Create together",
    imageAlt: "The Heidrun mobile — wooden hoops, antler, flowers, crystals and sea shells, seen from both sides",
    blocks: [
      { t: "p", x: "The mobile created for Heidrun began with a structure I had started to make, but it was never meant to be completed by me alone." },
      { t: "p", x: "It was created for her Mother Blessing, as she and Florian prepared to welcome their baby girl in October." },
      { t: "p", x: "Each person attending was invited to bring one small fragment. Something chosen for Heidrun, for the child who was coming, or simply something carrying a feeling, a memory or an intention they wanted to give." },
      { t: "p", x: "During the gathering, these fragments became part of the work.\nSome were attached by the person who had brought them. Others were placed together with me. Slowly, the piece changed in our hands.\nAnd that became part of its meaning." },
      { t: "img" },
      { t: "p", x: "The finished mobile does not simply represent Heidrun and the child she is carrying. It holds traces of the people who gathered around her at this particular threshold in her life — each leaving something of themselves within it." },
      { t: "pull", x: "No single fragment tells the story.\nTogether, they do." },
      { t: "p", x: "Alongside the physical work, I wrote an original fairytale for Heidrun and Florian — my own interpretation of their journey of fate: the paths that brought them to one another, the life they have created together, and the little girl now making her way into their story.\nThe fairytale became another layer of the artwork." },
      { t: "quote", kicker: "From the fairytale · The Wolf Woman and the Child of the Rose Stone", x: "Sometimes the greatest magic does not arrive with thunder.\nSometimes it begins at a secret festival in the mountains." },
      { t: "p", x: "One part can be touched.\nThe other can be told.\nOne carries objects.\nThe other carries words." },
      { t: "p", x: "Together they hold a moment that will never exist in quite the same way again." },
      { t: "p", x: "Heidrun's mobile became the first collective work of The Things We Keep — and the beginning of an idea that has stayed with me ever since:" },
      { t: "pull", x: "That an artwork does not always have to be created for someone.\nSometimes, it can be created with them." },
    ],
  },
  no: {
    eyebrow: "Det første felles verket",
    title: "Heidrun",
    sub: "Et verk laget av mange hender, som bærer én felles historie.",
    imageCaption: "Heidruns mobile, sett fra begge sider.",
    cta: "Start en bestilling",
    imageAlt: "Heidrun-mobileen — tverringer, gevir, blomster, krystaller og sjøskjell, sett fra begge sider",
    blocks: [
      { t: "p", x: "Mobileen laget for Heidrun begynte med en struktur jeg hadde begynt på, men den var aldri ment at jeg skulle fullføre alene." },
      { t: "p", x: "Den ble laget til Mother Blessing hennes, mens hun og Florian ventet babyjenta deres i oktober." },
      { t: "p", x: "Hver person som deltok, ble invitert til å bringe ett lite fragment. Noe valgt til Heidrun, til barnet som kom, eller rett og slett noe som bar på en følelse, et minne eller en hensikt de ville gi videre." },
      { t: "p", x: "I løpet av samlingen ble fragmentene en del av verket.\nNoen ble festet av personen som hadde brakt dem. Andre ble plassert sammen med meg. Sakte forandret verket seg i hendene våre.\nOg det ble en del av betydningen." },
      { t: "img" },
      { t: "p", x: "Den ferdige mobilen representerer ikke bare Heidrun og barnet hun bærer på. Den bærer spor av menneskene som samlet seg rundt henne ved denne terskelen i livet hennes — hver og én har lagt noe av seg selv i den." },
      { t: "pull", x: "Ingen enkelt fragment forteller historien.\nSammen gjør de det." },
      { t: "p", x: "Ved siden av det fysiske verket skrev jeg et eventyr til Heidrun og Florian — min egen tolkning av skjebneresen deres: veiene som brakte dem til hverandre, livet de har skapt sammen, og jenta som nå er på vei inn i historien deres.\nEventyret ble et nytt lag av verket." },
      { t: "quote", kicker: "Fra eventyret · Ulvekvinnen og barnet med rosesteinen", x: "Noen ganger kommer den største magien ikke med torden.\nNoen ganger begynner den på en hemmelig festival i fjellene." },
      { t: "p", x: "Én del kan røres.\nDen andre kan fortelles.\nÉn bærer gjenstander.\nDen andre bærer ord." },
      { t: "p", x: "Sammen bærer de et øyeblikk som aldri vil finnes på nøyaktig samme måte igjen." },
      { t: "p", x: "Heidruns mobile ble det første felles verket i The Things We Keep — og begynnelsen på en tanke som har blitt værende hos meg siden:" },
      { t: "pull", x: "At et verk ikke alltid må skapes for noen.\nNoen ganger kan det skapes sammen med dem." },
    ],
  },
};

export default function HeidrunPage() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const t = STORY[lang];

  useEffect(() => {
    document.title = "Heidrun — The Things We Keep";
    return () => {
      document.title =
        lang === "no"
          ? "The Things We Keep — Gjenstander med en historie"
          : "The Things We Keep — Objects with a story";
    };
  }, [lang]);

  return (
    <div className="bg-paper pb-24 pt-32 sm:pb-32 sm:pt-40" data-testid="heidrun-page">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="ttwk-label" data-testid="heidrun-eyebrow">
                {t.eyebrow}
              </p>
              <h1 className="mt-6 font-serif text-6xl font-light tracking-tight sm:text-7xl lg:text-8xl" data-testid="heidrun-title">
                {t.title}
              </h1>
              <p className="mt-6 font-serif text-2xl font-light italic text-terracotta sm:text-3xl">{t.sub}</p>
            </Reveal>
          </div>
        </div>

        <div className="mt-16 max-w-2xl">
          {t.blocks.map((b, i) => {
            if (b.t === "img") {
              return (
                <Reveal key={i}>
                  <div className="img-frame my-16 overflow-hidden rounded-sm border border-clay/60">
                    <img
                      src="/media/heidrun.jpg"
                      alt={t.imageAlt}
                      className="w-full object-cover"
                      data-testid="heidrun-image"
                    />
                  </div>
                  <p className="-mt-10 font-serif text-sm italic text-umber/60">{t.imageCaption}</p>
                </Reveal>
              );
            }
            if (b.t === "pull") {
              return (
                <Reveal key={i}>
                  <p className="my-14 text-center font-serif text-3xl font-light italic leading-snug text-terracotta sm:text-4xl">
                    {b.x.split("\n").map((line, j) => (
                      <span key={j}>
                        {j > 0 && <br />}
                        {line}
                      </span>
                    ))}
                  </p>
                </Reveal>
              );
            }
            if (b.t === "quote") {
              return (
                <Reveal key={i}>
                  <div className="my-14 text-center" data-testid="heidrun-fairytale-quote">
                    <p className="ttwk-label" style={{ color: "#8A4F3D" }}>
                      {b.kicker}
                    </p>
                    <p className="mx-auto mt-6 max-w-xl font-serif text-2xl font-light italic leading-snug text-espresso sm:text-3xl">
                      {b.x.split("\n").map((line, j) => (
                        <span key={j}>
                          {j > 0 && <br />}
                          {line}
                        </span>
                      ))}
                    </p>
                  </div>
                </Reveal>
              );
            }
            return (
              <Reveal key={i} delay={Math.min(i * 0.03, 0.15)}>
                <p className="mt-8 font-serif text-xl font-light leading-relaxed text-umber first:mt-0 sm:text-2xl">
                  {b.x.split("\n").map((line, j) => (
                    <span key={j}>
                      {j > 0 && <br />}
                      {line}
                    </span>
                  ))}
                </p>
              </Reveal>
            );
          })}
        </div>

        <Reveal>
          <button
            onClick={() => navigate("/", { state: { scrollTo: "commission" } })}
            className="mt-16 rounded-full bg-terracotta px-10 py-4 text-[11px] uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-espresso"
            data-testid="heidrun-cta"
          >
            {t.cta}
          </button>
        </Reveal>
      </div>
    </div>
  );
}
